/**
 * App Theme Scripts
 *
 * @package Göhler Tool
 * @author  pixit-webdesign
 */

import * as Debug from './utils/debug'
import { App_Materials_Controller } from '@app/controllers/materials-controller'
import { Toast } from './utils/toast'

import type { Created_Project, Todo_Project, Estimates_Project } from '../types/projects'
import type { Import_Material, Todo_Material } from '../types/materials'
import { Ctx } from '@app/context'
import { Create_Assembly_Presenter } from '@app/controllers/assembly-controller'
import { Spotlight_Controller } from '@app/controllers/spotlight-controller'
import Session_Guard from '@app/controllers/session-guard'
import { App_Times_Controller } from '@app/controllers/times-controller'
import { Goehler_Tool_REST_Client } from '@app/utils/rest-client'
import { handle_api_error } from '@app/utils/api-error'

export class App {
    ctx: Ctx|null = null
	post_id: string|null
	ajax_url: string
	nonce: string
    calendar_text = {
        days: [ 'SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA' ],
        dayNames: [ 'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag' ],
        months: [ 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember' ],
        monthsShort: [ 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez' ],
        today: 'heute',
        now: 'jetzt',
        weekNo: 'KW',
    }

    spotlight_controller: Spotlight_Controller
    tablet_mode: boolean = false

    materials_manager: App_Materials_Controller
    create_assembly_presenter: Create_Assembly_Presenter
    times_controller: App_Times_Controller

    rest_client: Goehler_Tool_REST_Client

	constructor() {
		this.post_id  = gt_ajax_object.post_id
		this.ajax_url = gt_ajax_object.ajax_url
		this.nonce    = gt_ajax_object.nonce

        // TODO: immer nötig?
        this.materials_manager = new App_Materials_Controller()
        this.create_assembly_presenter = new Create_Assembly_Presenter()
        this.spotlight_controller = Spotlight_Controller.init()
        this.times_controller = new App_Times_Controller()

        this.rest_client = new Goehler_Tool_REST_Client()

        jQuery(
            () => {
                this.ready()
            }
        )
	}

    ready() {
        console.debug( 'ready: App' )
        this.init_user_modals()
        this.init_user_settings()
        this.init_user_dropdowns()
        this.init_tool_nags()
        this.init_tabs()
        this.init_forms()
        this.init_checkboxes()
        this.init_actions()
        this.init_events()
        this.init_accordions()
        this.check_loaded_toast()

        this.materials_manager.ready()
        this.create_assembly_presenter.ready()
        this.times_controller.ready()


        console.debug( 'template:', gt_current_post.template )

        Session_Guard.get_instance()
    }

	init_accordions() {
		jQuery( '[data-modal="help"] .ui.accordion' )
			.accordion(
				{
					exclusive: false,
					onClose: function( this: JQuery<HTMLElement> ) {
						console.log( 'onClose', this )
                        app.pause_videos( this )
					}
				}
			)
	}

	init_events() {
		const project_documents = document.querySelector( '.project-documents-list' )
		if ( project_documents ) {
			project_documents.addEventListener(
				'click',
				( e ) => {
                    const target = e.target as HTMLElement
					console.log( 'clicked', target )

					if ( target.dataset.action === 'remove-project-document-input' ) {
						e.preventDefault()
						console.log( 'delete project file' )
						app.delete_project_document_input( target )
					}

					if ( target.dataset.action === 'add-project-document-input' ) {
						e.preventDefault()
						console.log( 'add project file' )
						app.create_add_project_document_input()
					}
				}
			)
		}
	}

	init_actions() {
		jQuery( 'button[data-action^="decrease"]' ).on( 'click', ( e ) => app.decrease_duration( e ) )
		jQuery( 'button[data-action^="increase"]' ).on( 'click', ( e ) => app.increase_duration( e ) )
        jQuery( '[data-action="show-user-notifications"]' ).on( 'click', () => app.init_planning_todo_modal() )
        jQuery( '[data-action="reload-page"]' ).on( 'click', () => window.location.reload() )
	}

	init_forms() {
        console.log( 'app: init forms' )
		jQuery( '#form-project-add' ).form(
			{
				fields: {
					'project-title': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Projektnamen angeben.'
							}
						]
					},
					'customer-street': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte eine Adresse (Straße, Hausnummer) für das Projekt angeben.'
							}
						]
					},
					'customer-zip-location': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte PLZ und Ort für das Projekt angeben.'
							}
						]
					},
					'customer-tel': {
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte eine Telefonnummer für das Projekt angeben.'
							}
						]
					},
					'project-price': {
						noNativeValidation: true,
						rules: [
							{
								type: 'notEmpty',
								prompt: 'Bitte einen Verkaufspreis für das Projekt angeben.'
							},
							{
								type: 'integer',
								prompt: 'Bitte einen vollen Eurobetrag ohne Cents als Verkaufspreis angeben.'
							}
						]
					},
				},
				errorFocus: false,
				revalidate: true,
				onSuccess: app.init_add_project_post,
				onFailure: Toast.show_invalid_form, // wichtig: return false nötig, sonst Submit Event.
			}
		)

        jQuery( '#form-project-return-add' ).form(
            {
                fields: {
                    'return-project-title': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte einen Projektnamen angeben.'
                            }
                        ]
                    },
                    'return-customer-street': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Adresse (Straße, Hausnummer) für das Projekt angeben.'
                            }
                        ]
                    },
                    'return-customer-zip-location': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte PLZ und Ort für das Projekt angeben.'
                            }
                        ]
                    },
                    'return-customer-tel': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Telefonnummer für das Projekt angeben.'
                            }
                        ]
                    },
                    'return-project-price': {
                        noNativeValidation: true,
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte einen Verkaufspreis für das Projekt angeben.'
                            },
                            {
                                type: 'integer',
                                prompt: 'Bitte einen vollen Eurobetrag ohne Cents als Verkaufspreis angeben.'
                            }
                        ]
                    },
                    'return-project-reason': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte einen Grund für die Reklamation angeben.'
                            }
                        ]
                    },
                },
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => {
                    e.preventDefault()

                    void app.api_add_return_project()
                },
                onFailure: Toast.show_invalid_form,
            }
        )
	}

	init_checkboxes() {
		jQuery( '#form-project-add .invisible.checkbox' ).checkbox(
			{
				uncheckable: true,
			}
		)

        jQuery( '#return-project-parts-required-checkbox' ).checkbox()
	}

	init_user_modals() {
		console.log( 'init user modals' )
		jQuery( '[data-modal="user-settings"]' ).modal( { autofocus: false } )
        jQuery( '[data-modal="help"]' ).modal(
			{
				autofocus: false,

                // @ts-expect-error falsches Overload.
				onHide( this: HTMLElement ) {
                    console.log( 'onHide help', this )
                    app.pause_videos( this )
                },
			}
		)
		jQuery( '[data-modal="changelog"]' ).modal( { autofocus: false } )
	}

    pause_videos( container: HTMLElement ) {
        const videos = Array.from( container.querySelectorAll( 'video' ) )
        console.log( videos )

        for ( const video of videos ) {
            video.pause()
        }
    }

	init_user_dropdowns() {
		jQuery( '.user-menu .dropdown' ).dropdown(
			{
                on: 'hover',
				action: 'hide',
				onChange: function( _value?: string, _text?: string, $choice?: JQuery<HTMLElement> ): void {
					console.debug( 'user menu onchange' )

                    if ( ! $choice ) {
                        return
                    }

					if ( $choice[0].dataset.menu === 'settings' ) {
						app.show_user_settings()
					}

					if ( $choice[0].dataset.menu === 'changelog' ) {
						app.show_changelog()
					}

                    if ( $choice[0].dataset.menu === 'bugreport' ) {
                        app.show_bugreport()
                    }

					if ( $choice[0].dataset.menu === 'help' ) {
						app.show_help()
					}
				}
			}
		)
	}

	init_tool_nags() {
		jQuery( '#daily-check' )
			.nag(
				{
					persist: false,
					key: new Date().toLocaleDateString( 'de-DE' ),
					duration: 0,
                    expires: 0,
					animation: {
						show: 'fade',
						hide: 'fade'
					}
				}
			)
			.on( 'click', () => app.init_planning_todo_modal() )

		jQuery( '#version-nag' )
			.nag(
				{
					persist: false,
					key: gt_info.version,
                    expires: 0,
				}
			)

		jQuery( '#paused-progress' )
			.nag(
				{
					persist: true,
					key: new Date().toLocaleDateString( 'de-DE' ),
					duration: 0,
				}
			)

		jQuery( '#whiteboard-info-nag' )
			.nag(
				{
					persist: false,
					key: 'whiteboard-info',
					duration: 0,
                    expires: 0,
				}
			)
	}

	init_tabs() {
		jQuery( '#form-project-add .tabular.menu .item' ).tab(
			{
				onBeforeChange: function( this: JQuery<HTMLElement>, tab: string ): boolean {
					console.log( 'tab change', tab )
                    return true
				},
				onVisible: function( this: JQuery<HTMLElement>, tab: string ): boolean {
					console.log( 'tab changed', tab )
                    return true
				},
			}
		)
	}

    /**
     * Sucht nach URL-Parameter für Toasts beim neuen Seitenaufruf.
     */
	check_loaded_toast() {
		const url_params = new URLSearchParams( window.location.search );

		if ( url_params && url_params.get( 'toast' ) === 'no-permission' ) {
			Toast.show_ajax_response(
				'Auf den angeforderten Bereich hast Du keinen Zugriff.',
				''
			)
		}

		if ( url_params && url_params.get( 'toast' ) === 'deleted-project' ) {
			Toast.show_ajax_response(
				'Das Projekt wurde erfolgreich gelöscht.',
				'success'
			)
		}
	}

    // TODO: deprecated
	show_toast_invalid_form() {
		const message = 'Einige Angaben sind noch fehlerhaft oder unvollständig.<br> Bitte die Formularhinweise beachten und erneut versuchen.'
		Toast.show_ajax_response( message, '', 'info' )
		return false
	}

    // TODO: deprecated
	show_toast_ajax_error( data: { toast: string; error: string; } ) {
		let message = data.toast

		if ( data.error ) {
			message += `<br>${data.error}`
		}

		jQuery.toast(
			{
				position: Toast.get_position(),
				message,
				newestOnTop: Toast.get_position().includes( 'bottom' ),
				displayTime: Toast.get_time(),
				showProgress: 'bottom',
				compact: false,
				class: 'error',
				showIcon: 'times',
			}
		)
	}

    // TODO: deprecated
	show_toast_ajax_response( message: string, status: string = 'success', icon: string|boolean = false ) {
		jQuery.toast(
			{
				message,
				position: Toast.get_position(),
				newestOnTop: Toast.get_position().includes( 'bottom' ),
				displayTime: Toast.get_time(),
				showProgress: 'bottom',
				compact: false,
				class: status,
				classProgress: ( status === '' ? 'info' : false ),
				showIcon: ( icon ? icon : ( status === 'success' ? 'checkmark' : false ) ),
			}
		)
	}

    enable_tablet_mode() {
        this.tablet_mode = true
        document.body.dataset.tabletMode = 'true'
        gt_user_settings.tablet_mode = '1'
        console.log( '[Tablet Mode] enabled' )
    }

    disable_tablet_mode() {
        this.tablet_mode = false
        delete document.body.dataset.tabletMode
        gt_user_settings.tablet_mode = '0'
        console.log( '[Tablet Mode] disabled' )
    }

	init_user_settings() {
        const project_tasks = jQuery( '#settings-auto-open-project-tasks' ).closest( '.checkbox' )

        project_tasks.checkbox(
            {
                onChecked: () => app.ajax_update_user_setting( 'auto_open_project_tasks', true ),
                onUnchecked: () => app.ajax_update_user_setting( 'auto_open_project_tasks', false ),
            }
        )

        if ( Object.hasOwn( gt_user_settings, 'auto_open_project_tasks' ) ) {
            const value = gt_user_settings.auto_open_project_tasks
            console.debug( '[User Settings] [Auto Open Project Tasks]', value )

            if ( value === '1' ) {
                project_tasks.checkbox( 'set checked' )
            }
        }

        const tablet_mode = jQuery( '#settings-tablet-mode' ).closest( '.checkbox' )

        tablet_mode.checkbox(
            {
                onChecked: () => app.ajax_update_user_setting( 'tablet_mode', true ),
                onUnchecked: () => app.ajax_update_user_setting( 'tablet_mode', false ),
            }
        )

        if ( Object.hasOwn( gt_user_settings, 'tablet_mode' ) ) {
            const value = gt_user_settings.tablet_mode
            console.debug( '[User Settings] [Tablet Mode]', value )

            if ( value === '1' ) {
                tablet_mode.checkbox( 'set checked' )
                this.enable_tablet_mode()
            }
        }

		const debug = jQuery( '#settings-debug' ).closest( '.checkbox' )

		debug.checkbox(
			{
				onChecked: () => app.ajax_update_user_setting( 'debug', true ),
				onUnchecked: () => app.ajax_update_user_setting( 'debug', false ),
			}
		)

		if ( gt_user_settings.enable_debug === '1' ) {
			debug.checkbox( 'set checked' )
			Debug.enable_debug()
			console.debug( '[User Settings] [Debug Mode]', true )
		}

		const toast_time = jQuery( '#settings-toast-time' )

		toast_time.dropdown(
			{
				onChange: app.update_toast_time,
			}
		)

		if ( Object.hasOwn( gt_user_settings, 'toast_time' ) ) {
			const value = gt_user_settings.toast_time
			console.debug( '[User Settings] [Toast Time]', value )

			if ( value !== 'default' && value !== '' ) {
				toast_time.dropdown( 'set selected', value, true )
			}
		}

		console.debug( 'toast time', gt_toast.display_time )

		const toast_position = jQuery( '#settings-toast-position' )

		toast_position.dropdown(
			{
				onChange: app.update_toast_position,
			}
		)

		if ( Object.hasOwn( gt_user_settings, 'toast_position' ) ) {
			const value = gt_user_settings.toast_position
			console.debug( '[User Settings] [Toast Position]', value )

			if ( value !== 'default' && value !== '' ) {
				toast_position.dropdown( 'set selected', value, true )
			}
		}

		const toast_button = document.querySelector( '.toast-test' )
		if ( toast_button ) {
			toast_button.addEventListener(
				'click',
				() => {
					Toast.show_ajax_response(
						'Hier ist eine Testbenachrichtigung.'
					)
				}
			)
		}

		const list_limit = jQuery( '#settings-list-limit' )

		list_limit.dropdown(
			{
				onChange: app.update_list_limit,
			}
		)

		if ( Object.hasOwn( gt_user_settings, 'list_limit' ) ) {
			const value = gt_user_settings.list_limit
			console.debug( '[User Settings] [List Limit]', value )

			if ( value !== 'default' && value !== '' ) {
				list_limit.dropdown( 'set selected', value, true )
			}
		}
	}


	update_toast_time( value?: string, text?: string, $choice?: JQuery ): void {
		console.debug( 'update_toast_time', value, text, $choice )
		app.ajax_update_user_setting( 'toast_time', value )
	}

	update_toast_position( value?: string, text?: string, $choice?: JQuery ): void {
		console.debug( 'update_toast_position', value, text, $choice )
		app.ajax_update_user_setting( 'toast_position', value )
	}

	update_list_limit( value?: string, text?: string, $choice?: JQuery ) {
		console.log( value, text, $choice )
		app.ajax_update_user_setting( 'list_limit', value )
	}


	ajax_update_user_setting( setting: string, value?: boolean|string ) {
		console.debug( 'update_user_setting', setting, value )

		if ( ! [
            'debug',
            'auto_open_project_tasks',
            'tablet_mode',
            'toast_time',
            'toast_position',
            'list_limit'
        ].includes( setting ) ) {
			return
		}

		const { nonce, ajax_url } = gt_ajax_object

		const data = {
			action: 'update_user_setting',
			nonce,
			setting,
			value,
		}

        console.log( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.debug( 'AJAX response', response )

				switch ( response.setting ) {
					case 'debug':
						if ( response.value === '1' ) {
							Debug.enable_debug()
						} else {
							Debug.disable_debug()
						}
						break
					case 'toast_time':
						gt_user_settings.toast_time = ( response.value !== 'default' ? response.value : gt_toast.display_time )
						break
					case 'toast_position':
						gt_user_settings.toast_position = ( response.value !== 'default' ? response.value : gt_toast.position )
						break
					case 'list_limit':
						gt_user_settings.list_limit = ( response.value !== 'default' ? response.value : gt_list_settings.limit )
						jQuery( '#list-limit' ).dropdown( 'set selected', gt_user_settings.list_limit )
						break
                    case 'auto_open_project_tasks':
                        gt_user_settings.auto_open_project_tasks = response.value
                        break
                    case 'tablet_mode':
                        if ( response.value === '1' ) {
                            this.enable_tablet_mode()
                        } else {
                            this.disable_tablet_mode()
                        }
                        break;
				}

				console.debug( 'user settings', gt_user_settings )

				Toast.show_ajax_response(
					'Einstellung wurde geändert',
					'success',
					'checkmark'
				)
			}
		);
	}

	is_debug_enabled() {
		return gt_user_settings.enable_debug === '1'
	}

	show_user_settings() {
		console.log( 'show user settings' )
		const modal = jQuery( '[data-modal="user-settings"]' )
		modal.modal( 'show' )
	}

	show_changelog() {
		console.log( 'show changelog' )
		const modal = jQuery( '[data-modal="changelog"]' )
		modal.modal( 'show' )
	}

    show_bugreport() {
        console.log( 'show bugreport' )

        const modal = jQuery( '[data-modal="bugreport"]' )
        modal.modal( 'show' )
    }

	show_help() {
		console.log( 'show help' )
		const modal = jQuery( '[data-modal="help"]' )
		console.log( modal )
		modal.modal( 'show' )
	}

	init_planning_todo_modal() {
		this.reset_planning_todo()
		this.ajax_load_notification_projects()
		const modal = jQuery( '[data-modal="planning-todo"]' )
		modal.modal( { autofocus: false } ).modal( 'show' )

		jQuery( '#daily-check' ).nag( 'dismiss' )
	}

	reset_planning_todo() {
		const todo = document.querySelector( '.planning-todo' )

        if ( ! todo ) {
            return
        }

        todo.classList.remove( 'loaded' )

		const spinner = document.querySelectorAll( '.planning-todo .ui.loader' )
		for ( const el of Array.from( spinner ) ) {
			el.classList.remove( 'disabled' )
		}

		this.reset_planning_todo_tables()
	}

	reset_planning_todo_tables() {
		const tbody = document.querySelectorAll( '.planning-todo-container tbody' )
		for ( const el of Array.from( tbody ) ) {
			el.innerHTML = ''
		}
	}

	set_planning_todo_loading() {
		const todo = document.querySelector( '.planning-todo' )

        if ( ! todo ) {
            return
        }

		todo.classList.add( 'loading' )

		const spinner = document.querySelectorAll( '.planning-todo .ui.loader' )
		for ( const el of Array.from( spinner ) ) {
			el.classList.add( 'active' )
		}
	}

	set_planning_todo_loaded() {
		const todo = document.querySelector( '.planning-todo' )

        if ( ! todo ) {
            return
        }

		todo.classList.add( 'loaded' )
		todo.classList.remove( 'loading' )

		const spinner = document.querySelectorAll( '.planning-todo .ui.loader' )
		for ( const el of Array.from( spinner ) ) {
			el.classList.remove( 'active' )
			el.classList.add( 'disabled' )
		}
	}

	ajax_load_notification_projects() {
		const data = {
			action: 'load_notification_projects',
			nonce: app.nonce,
			post_id: app.post_id,
		}

		this.set_planning_todo_loading()

		jQuery.ajax(
			{
				url: app.ajax_url,
				type: 'POST',
				dataType: 'json',
				data,
				cache: false
			}
		).done(
			( response ) => {
				console.debug( response )

				this.set_planning_todo_loaded()

				this.render_planning_todo( response )
			}
		)
	}

	render_planning_todo( response: { estimates: Estimates_Project[], measurement: Todo_Project[]; construction_plans: Todo_Project[]; materials: Todo_Material[]; } ) {
		console.log( 'render planning todo', response )

		let tbody = document.querySelector( 'table[data-type="measurement"] tbody' )

		if ( tbody && response.measurement ) {
			for ( const project of response.measurement ) {
				this.render_planning_task_project( project, tbody )
			}
		}

		tbody = document.querySelector( 'table[data-type="construction-plans"] tbody' )

		if ( tbody && response.construction_plans ) {
			for ( const project of response.construction_plans ) {
				this.render_planning_task_project( project, tbody )
			}
		}

		tbody = document.querySelector( 'table[data-type="materials"] tbody' )

		if ( tbody && response.materials ) {
			for ( const material of response.materials ) {
				this.render_planning_task_material( material, tbody )
			}
		}

        tbody = document.querySelector( 'table[data-type="estimates"] tbody' )

        if ( tbody && response.estimates ) {
            for ( const project of response.estimates ) {
                this.render_planning_estimates_project( project, tbody )
            }
        }

		jQuery( '[data-modal="planning-todo"]' ).modal( 'refresh' )
	}

	render_planning_task_material( material: Todo_Material, tbody: Element ) {
		const tr = document.createElement( 'tr' )

		let td = document.createElement( 'td' )

		td.classList.add( 'selectable' )
		td.dataset.cell = 'project-title'

		td.innerHTML = `<a href="${material.project.permalink}?action-tab=planning">${material.project.title}`

		tr.appendChild( td )

		td = document.createElement( 'td' )
		td.dataset.cell = 'material-title'

		td.innerHTML = `${material.title}`

		tr.appendChild( td )

		td = document.createElement( 'td' )
		td.classList.add( 'center', 'aligned' )
		td.dataset.cell = 'material-delivery-date'

		td.innerHTML = `<i class="calendar alternate outline icon"></i> ${format_date( material.date ) }`

		tr.appendChild( td )

		tbody.appendChild( tr )
	}

	render_planning_task_project( project: Todo_Project, tbody: Element ) {
		const tr = document.createElement( 'tr' )

		let td = document.createElement( 'td' )

		td.classList.add( 'selectable' )
		td.dataset.cell = 'project-title'

		td.innerHTML = `<a href="${project.permalink}?action-tab=planning">${project.title}`

		tr.appendChild( td )

		td = document.createElement( 'td' )
		td.classList.add( 'center', 'aligned' )
		td.dataset.cell = 'project-task-date'

		td.innerHTML = `<i class="calendar alternate outline icon"></i> ${format_date( project.date ) }`

		tr.appendChild( td )

		tbody.appendChild( tr )
	}

    render_planning_estimates_project( project: Estimates_Project, tbody: Element ) {
        const tr = document.createElement( 'tr' )

        let td = document.createElement( 'td' )

        td.classList.add( 'selectable' )
        td.dataset.cell = 'project-title'

        td.innerHTML = `<a href="${project.permalink}?action-tab=planning">${project.title}`

        tr.appendChild( td )

        td = document.createElement( 'td' )
        td.classList.add( 'center', 'aligned' )
        td.dataset.cell = 'project-estimates'

        td.innerHTML = project.estimates ? '<i class="green check icon"></i>' : '<i class="red times icon"></i>'

        tr.appendChild( td )

        td = document.createElement( 'td' )
        td.classList.add( 'center', 'aligned' )
        td.dataset.cell = 'project-offer-estimates'

        td.innerHTML = project.offer_estimates ? '<i class="green check icon"></i>' : '<i class="red times icon"></i>'

        tr.appendChild( td )

        tbody.appendChild( tr )
    }

	decrease_duration( e: JQuery.ClickEvent ) {
		console.debug( 'decrease project estimate', e.currentTarget )
		const dropdown = jQuery( e.currentTarget ).next()

		let value: string|string[] = dropdown.dropdown( 'get value' )

		if ( ! value || typeof value !== 'string' ) {
			return
		}

		const time: string[] = value.split( ':' )

		let hours: number   = Number( time[0] )
		let minutes: number|string = Number( time[1] ) // string wegen 00

		if ( hours > 0 && minutes === 0 ) {
			hours   = hours - 1
			minutes = '30'
		} else if ( minutes <= 30 ) {
			minutes = '00'
		} else {
			minutes = '30'
		}

		value = `${hours}:${minutes}`

		console.debug( value )

		if ( value === '0:00' ) {
			dropdown.dropdown( 'clear' )
		} else {
			dropdown.dropdown( 'set selected', value )
		}
	}

	increase_duration( e: JQuery.ClickEvent ) {
		console.debug( 'increase project estimate', e.currentTarget )
		const dropdown = jQuery( e.currentTarget ).prev()

		const value: string|string[] = dropdown.dropdown( 'get value' )

		if ( ! value || typeof value !== 'string' ) {
			dropdown.dropdown( 'set selected', '0:30' )
			return
		}

		const time: string[] = value.split( ':' )

		let hours: number   = Number( time[0] )
		let minutes: number|string = Number( time[1] )

		if ( minutes < 30 ) {
			minutes = '30'
		} else {
			minutes = '00'
			hours   = hours + 1
		}

		console.debug( `${hours}:${minutes}` )

		dropdown.dropdown( 'set selected', `${hours}:${minutes}` )
	}

	parse_materials_import( files: FileList | null ) {
		console.log( 'parse materials import', files )

        if ( ! files ) {
            console.log( 'keine Files zum Parsen' )
            return
        }

		const config = {
			header: false,
			skipEmptyLines: true,
			delimiter: ';',
			complete: app.parse_csv_data,
		}

		Papa.parse( files[0], config )
	}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
	parse_csv_data( results: any, _file: any ): void {
		console.log( 'results', results.data )

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
		const data: any[]   = results.data // TODO
		const length: number = data.length

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const materials: any[] = [] // TODO

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
		let header_array: any[] = [] // TODO

		let material_index: number = -1

		console.log( 'erste Zeile', data[0] )

		if ( data[0].length === 1 ) {
			Toast.show_ajax_response( 'Ungültiges Format: Kein Semikolon als Separator in der Kopfzeile gefunden.', 'error' )
			return
		}

		for ( let i = 0; i < length; i++ ) {
			console.log( 'result', data[i] )

			const is_header = data[i].some(
				( item: string ): boolean => item.toLowerCase().includes( 'länge' ) || item === 'Beschlag'
			)

			if ( is_header ) {
				header_array   = data[i]
				material_index = data[i].findIndex( ( column: string ): boolean => column.toLowerCase().includes( 'material' ) )
				console.log( 'material index', material_index )
				continue
			}

			if ( header_array.length === 0 ) {
				console.log( 'data error', data[i], is_header )
				Toast.show_ajax_response( 'Ungültiges Format: Importierte CSV hat keine kompatiible(n) Kopfzeile(n)', 'error' )
				return
			}

			if ( header_array[1] === 'Anzahl' && Number( data[i][1] ) < 1 ) {
				continue
			}

			const material: Import_Material = {
				id: data[i][0],
				type: header_array[0].trim(),
				properties: {},
                name: null,
			}

			if ( material_index > -1 ) {
				material.name = data[i][material_index]
			}

			for ( const [ index, header ] of header_array.entries() ) {
				console.log( 'header', header, header_array, index, data[i][index] )

				if ( index === 0 || header.length === 0 || data[i][index].length === 0 || header.toLowerCase().includes( 'material' ) ) {
					continue
				}

				material.properties[header] = data[i][index]
			}

			materials.push( material )
			app.materials_manager.render_material_preview( material, i )
		}

		console.log( materials )
	}

	create_added_project_item( data: Created_Project ) {
		const container = document.querySelector( '.create-form-content .project-list' )

        if ( ! container ) {
            return
        }

		const li = document.createElement( 'li' )

		li.classList.add( 'project-list-item' )

		li.innerHTML = `
            <div class="item-title">
                <a href="${data.permalink}?action-tab=planning">${data.post_title}</a>
            </div>
                
            <div class="item-status">
                <span class="badge status" data-project-status="planung">Planung</span>
            </div>
            `

		container.append( li )
		li.classList.add( 'is-visible' )

		container.scrollIntoView( { block: 'start', behavior: 'smooth' } )
	}

	create_add_project_document_input() {
		console.log( 'add project document input' )

		const documents = document.querySelector( '.project-documents-list' )

        if ( ! documents ) {
            return
        }

		const id = Date.now()
		const container = documents.querySelector( '.files-list' )

        if ( ! container ) {
            return
        }

		const file = document.createElement( 'div' )

		file.classList.add( 'inline', 'fields', 'project-document-fields' )
		file.innerHTML = `
		<div class="six wide field">
			<div class="ui labeled input">
				<label for="project-document-titles[${id}]" class="ui label"></label>
				<input type="text" class="form-control" name="project-document-titles[${id}]" id="project-document-titles[${id}]" placeholder="Name eingeben... (optional)"/>
			</div>
		</div>

		<div class="ten wide field">
			<div class="ui file input">
				<input type="file" name="project-document-${id}" id="project-document-${id}">
			</div>

			<button type="button" class="ui red icon button" data-action="remove-project-document-input">
				<i class="times icon"></i>
			</button>
		</div>
	`
		container.append( file )

		// Form Validation Fix für neue DOM Input
		jQuery( '#form-project-add' ).form( 'set optional', `#project-document-titles[${id}]`, true )
		jQuery( '#form-add-project-documents' ).form( 'set optional', `#project-document-titles[${id}]`, true )
	}

	delete_project_document_input( target: Element ) {
		console.log( 'delete project file input', target )

		const file = target.closest( '.project-document-fields' )

        if ( file ) {
            file.remove()
        }
	}

	clear_add_project_documents_list() {
		const container = document.querySelector( '#form-project-add .files-list' ) as HTMLElement | null

        if ( ! container ) {
            return
        }

		container.innerHTML = ''
	}

	clear_add_project_materials_list() {
		const container = document.querySelector( '#form-project-add .import-preview-list' ) as HTMLElement | null

        if ( ! container ) {
            return
        }

		container.innerHTML = ''
	}

	init_add_project_post( e: Event ) {
		console.log( 'new project form submit', e )

		// enable AJAX.
		e.preventDefault()

		const form: JQuery<this> = jQuery( this )

		// disable form submit button.
		jQuery( '.submit', form ).attr( 'disabled', 'disabled' );

		console.debug( form.serialize() )

		// create new post with form data.
		app.ajax_add_project_post( form )
	}

	/**
	 * Sendet Formulardaten für neues Projekt via AJAX ans Plugin. Bekommt Daten des neuen Projekts zurück.
	 */
	ajax_add_project_post( form: JQuery<HTMLFormElement> ) {
		const { ajax_url, nonce } = gt_ajax_object

		const data = new FormData( form[0] )
		data.append( 'action', 'add_project' )
		data.append( 'nonce', nonce )

		console.debug( 'AJAX data', data )

		jQuery.ajax(
			{
				url: ajax_url,
				type: 'POST',
				data,
				cache: false,
				contentType: false,
				processData: false,
			}
		).done(
			( response ) => {
				console.log( 'AJAX response', response )

				// re-enable form submit button.
				jQuery( '.submit', form ).removeAttr( 'disabled' );

				if ( response.success === false ) {
					Toast.show_ajax_error( response )
					return
				}

				if ( response.toast !== '' ) {
					Toast.show_ajax_response( response.toast )
				}

				if ( response.error === '' ) {
					form.form( 'reset' )

					app.clear_add_project_documents_list()
					app.create_add_project_document_input()

					app.clear_add_project_materials_list()

					app.create_added_project_item( response.post )
				}
			}
		);
	}

    /**
     * Hilfsfunktion um ein Modal-Element als jQuery-Objekt zu finden.
     */
    get_modal( selector: string|null = null ): JQuery<HTMLElement>|null {
        if ( ! selector ) {
            return null
        }

        return jQuery( `.ui.modal[data-modal="${selector}"]` )
    }

    /**
     * Hilfsfunktion um alle Modal-Elemente zu schließen (insbesondere nach AJAX Response).
     */
	close_modals() {
		const modals = jQuery( '.ui.modal' )
		modals.modal( 'hide' )
	}

    play_confetti( type: string ) {
        switch ( type ) {
            case 'stars': {
                const defaults = {
                    spread: 360,
                    ticks: 120,
                    gravity: 0,
                    decay: 0.96,
                    startVelocity: 30,
                    colors: [ 'FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8' ],
                    zIndex: 99999,
                };

                function shoot() {
                    confetti(
                        {
                            ...defaults,
                            particleCount: 70,
                            scalar: 1.2,
                            shapes: [ 'star' ],
                        }
                    )

                    confetti(
                        {
                            ...defaults,
                            particleCount: 40,
                            scalar: 0.75,
                            shapes: [ 'circle' ],
                        }
                    )
                }

                setTimeout( shoot, 0 )
                setTimeout( shoot, 100 )
                setTimeout( shoot, 200 )
                break
            }

            case 'money': {
                console.log( 'money confetti' )

                const shape = confetti.shapeFromPath(
                    {
                        path: 'M64 173.5L64 483.6C64 503 75.8 520.9 94.3 526.9C188.3 556.9 253.1 535.3 317.6 513.8C380 493 442 472.3 529.7 498.5C551.9 505.1 575.9 489.7 575.9 466.5L575.9 156.4C575.9 137 564.1 119.1 545.6 113.1C451.6 83.1 386.8 104.7 322.3 126.2C259.9 147 197.9 167.7 110.2 141.5C88 134.9 63.9 150.3 63.9 173.5zM320 432C267 432 224 381.9 224 320C224 258.1 267 208 320 208C373 208 416 258.1 416 320C416 381.9 373 432 320 432zM191.1 469.5C191.8 473.9 188.3 477.6 183.9 477.6C168.2 477.6 151.8 475.8 133.9 471.5C130.4 470.7 127.9 467.5 127.9 463.8L128 424C128 419.6 131.6 415.9 136 416.5C164.1 420 186.6 441.7 191.2 469.5zM512 418.6C512 423.6 507.4 427.4 502.5 426.6C487.1 424.1 472.3 422.7 458.1 422.3C453.2 422.2 449.4 417.8 450.9 413.1C458.2 389.4 478.9 371.7 504.1 368.5C508.5 368 512.1 371.6 512.1 376L512.1 418.6zM504 223.5C475.9 220 453.4 198.3 448.8 170.5C448.1 166.1 451.6 162.4 456 162.4C471.7 162.4 488.1 164.2 506 168.5C509.5 169.3 512 172.5 512 176.2L512 216.1C512 220.5 508.4 224.2 504 223.6zM181.9 217.7C186.8 217.8 190.6 222.2 189.1 226.9C181.8 250.6 161.1 268.3 135.9 271.5C131.5 272 127.9 268.4 127.9 264L127.9 221.4C127.9 216.4 132.5 212.6 137.4 213.4C152.8 215.9 167.6 217.3 181.8 217.7zM304 252C293 252 284 261 284 272C284 281.7 290.9 289.7 300 291.6L300 340L296 340C285 340 276 349 276 360C276 371 285 380 296 380L344 380C355 380 364 371 364 360C364 349 355 340 344 340L340 340L340 272C340 261 331 252 320 252L304 252z',
                        matrix: [ 0.03597122302158273, 0, 0, 0.03597122302158273, -4.856115107913669, -5.071942446043165 ]
                    }
                )

                const defaults = {
                    scalar: 2,
                    spread: 180,
                    particleCount: 200,
                    origin: { y: 0 },
                    startVelocity: -35,
                    zIndex: 99999,
                };

                confetti(
                    {
                        ...defaults,
                        shapes: [ shape ],
                        colors: [ '#74ea75', '#289329', '#3dde18' ]
                    }
                )
                break
            }
        }
    }

    /**
     * Legt ein Reklamationsprojekt über die REST-API an und
     * leitet anschließend in die Montage des neuen Projekts.
     */
    async api_add_return_project() {
        console.log( '[REST] add return project' )

        const TASK = 'Anlegen des Reklamationsprojekts'

        const form = jQuery( '#form-project-return-add' )

        const checkbox = form.find( '#return-project-parts-required-checkbox' )

        const data: Record<string, unknown> = {
            title: String( form.find( '#return-project-title' ).val() ).trim(),
            street: String( form.find( '#return-customer-street' ).val() ).trim(),
            zip_location: String( form.find( '#return-customer-zip-location' ).val() ).trim(),
            tel: String( form.find( '#return-customer-tel' ).val() ).trim(),
            email: String( form.find( '#return-customer-email' ).val() ).trim(),
            price: Number( form.find( '#return-project-price' ).val() ),
            notes: String( form.find( '#return-project-global-notes' ).val() ).trim(),
            reason: String( form.find( '#return-project-reason' ).val() ).trim(),
            parts_required: checkbox.checkbox( 'is checked' ) as boolean,
        }

        const button = form.find( '.submit.button' )

        button.addClass( 'loading disabled' ).prop( 'disabled', true )

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/projects/add-return',
                data
            ) as { toast: string, redirect: string }

            console.log( `${ TASK }?`, response )

            if ( response.redirect ) {
                window.location.href = response.redirect
                return
            }

            app.show_toast_ajax_response( response.toast )
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            button.removeClass( 'loading disabled' ).prop( 'disabled', false )
        }
    }
}

